import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { TILE_URL, TILE_ATTRIBUTION, TILE_SUBDOMAINS } from './mapTiles';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { FaMapMarkerAlt, FaChevronRight, FaCheckCircle, FaExclamationTriangle, FaClock } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import placeholderImage from '../../../assets/placeholder.png';
import 'leaflet/dist/leaflet.css';
import './PropertyMap.css';

// Fix Leaflet default icon issue in webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;

// Custom CloudSafe pin icon
const createPinIcon = (status) => {
  const colors = {
    compliant: '#16a34a',
    'due-soon': '#d97706',
    overdue: '#dc2626',
    unknown: '#1A80E5',
  };
  const color = colors[status] || colors.unknown;

  return L.divIcon({
    className: 'cs-map-pin',
    html: `<div style="
      width: 28px; height: 28px;
      background: ${color};
      border: 3px solid #fff;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      display: flex; align-items: center; justify-content: center;
    "><div style="
      width: 8px; height: 8px;
      background: #fff;
      border-radius: 50%;
      transform: rotate(45deg);
    "></div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Component to auto-fit bounds
const FitBounds = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 0) return;
    if (positions.length === 1) {
      map.setView(positions[0], 14);
    } else {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [map, positions]);

  return null;
};

const PropertyMap = ({ properties, onPropertyClick, triggerGeocoding, onGeocode, renderPopupActions }) => {
  const mapRef = useRef(null);
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);

  // Filter properties with valid coordinates
  const mappable = useMemo(
    () => properties.filter(p => p.latitude && p.longitude && !isNaN(p.latitude) && !isNaN(p.longitude)),
    [properties]
  );
  const unmappable = properties.length - mappable.length;

  const positions = useMemo(
    () => mappable.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]),
    [mappable]
  );

  // Default center: London
  const defaultCenter = [51.5074, -0.1278];
  const center = positions.length > 0 ? positions[0] : defaultCenter;

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return placeholderImage;
    if (imageUrl.startsWith('http')) return imageUrl;
    if (imageUrl.startsWith('uploads/')) return getApiEndpoint(`/api/${imageUrl}`);
    return getApiEndpoint(`/api/uploads/${imageUrl}`);
  };

  const handleGeocode = useCallback(async () => {
    if (geocodingInProgress) return;
    setGeocodingInProgress(true);
    try {
      if (onGeocode) {
        await onGeocode();
      } else {
        const token = localStorage.getItem('token');
        await fetch(getApiEndpoint('/api/properties/batch-geocode'), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
      }
      if (triggerGeocoding) triggerGeocoding();
    } catch (err) {
      console.error('Geocoding failed:', err);
    } finally {
      setGeocodingInProgress(false);
    }
  }, [geocodingInProgress, triggerGeocoding, onGeocode]);

  const statusLabel = (status) => {
    const map = {
      compliant: { label: 'Compliant', cls: 'popup-status-green' },
      'due-soon': { label: 'Due Soon', cls: 'popup-status-amber' },
      overdue: { label: 'Overdue', cls: 'popup-status-red' },
    };
    return map[status] || { label: 'No Data', cls: 'popup-status-gray' };
  };

  return (
    <div className="property-map-wrapper">
      {unmappable > 0 && (
        <div className="pm-notice">
          <FaMapMarkerAlt />
          <span>{unmappable} {unmappable === 1 ? 'property' : 'properties'} could not be placed on the map.</span>
          <button type="button" className="pm-geocode-btn" onClick={handleGeocode} disabled={geocodingInProgress}>
            {geocodingInProgress ? 'Locating...' : 'Auto-locate'}
          </button>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={12}
        className="property-map-container"
        ref={mapRef}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} subdomains={TILE_SUBDOMAINS} />
        <FitBounds positions={positions} />

        {mappable.map((property) => {
          const st = statusLabel(property.compliance_status);
          return (
            <Marker
              key={property.id}
              position={[parseFloat(property.latitude), parseFloat(property.longitude)]}
              icon={createPinIcon(property.compliance_status)}
            >
              <Popup className="cs-popup" maxWidth={260} minWidth={220}>
                <div className="pm-popup">
                  <div className="pm-popup-thumb">
                    <img
                      src={getImageUrl(property.image_url)}
                      alt={property.name}
                      onError={(e) => { e.target.src = placeholderImage; }}
                    />
                  </div>
                  <div className="pm-popup-body">
                    <h4 className="pm-popup-name">{property.name}</h4>
                    <p className="pm-popup-address">{property.address}</p>
                    <div className="pm-popup-meta">
                      <span className={`pm-popup-status ${st.cls}`}>{st.label}</span>
                      {property.overdue_count > 0 && (
                        <span className="pm-popup-badge pm-badge-red">{property.overdue_count} overdue</span>
                      )}
                      {property.due_soon_count > 0 && (
                        <span className="pm-popup-badge pm-badge-amber">{property.due_soon_count} due soon</span>
                      )}
                    </div>
                    {renderPopupActions ? renderPopupActions(property) : (
                      <button
                        type="button"
                        className="pm-popup-cta"
                        onClick={() => onPropertyClick(property.id)}
                      >
                        Open Property <FaChevronRight size={10} />
                      </button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default PropertyMap;
